import { PencilIcon } from '@heroicons/react/24/outline';
import React from 'react';

import { DiscriminatedStaffNoteUpdatedEvent } from '@/services/api/generated/matterChangesService';

import {
  ActivityBody,
  ActivityDescription,
  ActivityIcon,
  ActivityItem,
} from '../activity-feed-v2';
import { getUserFromEvent } from '../activity-feed-v2/ActivityDiffList';

export interface StaffNoteUpdatedEventV2Props {
  event: DiscriminatedStaffNoteUpdatedEvent;
}

/**
 * V2 renderer for Staff Note Updated events that uses the `previous` field
 * to show the updated content, instead of parsing the diff format.
 */
export const StaffNoteUpdatedEventV2: React.FC<
  StaffNoteUpdatedEventV2Props
> = ({ event }) => {
  const { content, staff_note, previous } = event.payload;
  const currentContent = content || staff_note?.content;
  const previousContent = previous?.content;
  const user = getUserFromEvent(event);

  const icon = (
    <ActivityIcon
      icon={
        <div className="flex h-6 w-6 items-center justify-center">
          <PencilIcon className="h-4 w-4 text-gray-400" />
        </div>
      }
    />
  );

  // Determine if this is a significant change (more than just typos)
  const isSignificantChange =
    !previousContent ||
    Math.abs((currentContent?.length || 0) - (previousContent?.length || 0)) >
      50;

  const body = (
    <ActivityBody>
      {currentContent && (
        <div className="space-y-2">
          {/* Show a preview of the current content */}
          <ActivityDescription>
            <div className="text-sm text-gray-600 italic">
              &ldquo;
              {currentContent.length > 100
                ? `${currentContent.substring(0, 100)}...`
                : currentContent}
              &rdquo;
            </div>
          </ActivityDescription>

          {/* If there was a previous version and it's a significant change, note that */}
          {previousContent && isSignificantChange && (
            <div className="text-xs text-gray-500">
              Content updated ({currentContent.length} characters, was{' '}
              {previousContent.length} characters)
            </div>
          )}

          {/* For small changes, could show inline diff */}
          {previousContent && !isSignificantChange && (
            <div className="text-xs text-gray-500">Minor edit</div>
          )}
        </div>
      )}
    </ActivityBody>
  );

  return (
    <ActivityItem
      icon={icon}
      title="Note Updated"
      timestamp={new Date(event.occurred_at ?? new Date())}
      user={user}
      body={body}
    />
  );
};
